import React, { useState, useEffect, useMemo } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  Box,
  styled,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  ListItem,
  ListItemText,
  Avatar,
  Button,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemButton,
  FormControlLabel,
  Chip,
  SelectChangeEvent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  PeopleOutline as PeopleIcon,
  PendingOutlined as PendingIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import ownAuthSession from '../../services/auth/ownAuthSession';
import firebaseDataService from '../../services/FirebaseDataService';
import { taskDataService } from '../../services/taskDataService';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList
} from 'recharts';
import { MapContainer, TileLayer, Marker, Popup, useMap, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MissedTasksModal from './MissedTasksModal';
import CompletedTasksModal from './CompletedTasksModal';
import PendingTasksModal from './PendingTasksModal';
import AllCompletedTasksModal from './AllCompletedTasksModal';
import { useNavigate } from 'react-router-dom';
import SurveyCharts from './SurveyCharts';
import TaskStatusChart from './TaskStatusChart';
import WeeklyTaskStatusChart from './WeeklyTaskStatusChart';
import MonthlyTaskStatusChart from './MonthlyTaskStatusChart';
import PersonnelPerformanceChart from './PersonnelPerformanceChart';
import WeeklyPersonnelPerformanceChart from './WeeklyPersonnelPerformanceChart';
import MonthlyPersonnelPerformanceChart from './MonthlyPersonnelPerformanceChart';
import WidgetSelectorModal from './WidgetSelectorModal';
import StatsCards from './StatsCards';
import { Widget } from './WidgetSelectorModal'; // Widget türünü import et
import TaskGroupsModal from '../tasks/modals/TaskGroupsModal'; 
import { useTranslation } from 'react-i18next';

// Leaflet için marker icon düzeltmeleri
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Marker icon with custom color
const createColoredMarkerIcon = (color = 'blue') => {
  return new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

// Map view ayarlamak için özel komponent
interface MapViewProps {
  center: L.LatLngExpression;
  zoom: number;
}

// Dashboard bileşeni için props tanımı
interface DashboardProps {
  branchId?: string | null;
  isManager?: boolean;
}

function MapView({ center, zoom }: MapViewProps) {
  const map = useMap();
  if (Array.isArray(center)) {
    map.setView(center as [number, number], zoom);
  } else {
    map.setView([center.lat, center.lng], zoom);
  }
  return null;
}

// Tema renkleri (mobile uyumlu)
const THEME_COLORS = {
  personnel: '#1976D2', // Mavi
  tasks: '#9C27B0',     // Mor
  pending: '#FF9800',   // Turuncu
  completed: '#4CAF50'  // Yeşil
};

// Kaydırılabilir ana içerik için styled component
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 64px)', // Header yüksekliğini (varsayılan 64px) çıkarıyoruz
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  overflow: 'hidden',
  '& .MuiCardHeader-root': {
    padding: theme.spacing(1),
    '& .MuiTypography-h6': {
      fontSize: '1rem',
      fontWeight: 'bold',
    },
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(1),
  },
}));

// İstatistik kartı için styled component - tıklanabilir kart
const StatsCard = styled(Paper)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
    background: `linear-gradient(to right, white, ${bgcolor}10)`,
  },
}));

const IconBox = styled(Box)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgcolor,
  marginRight: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    color: 'white',
    fontSize: 24,
  },
}));

const StatsText = styled(Box)({
  flexGrow: 1,
  '& .MuiTypography-h4': {
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  '& .MuiTypography-body2': {
    fontSize: '0.75rem',
  },
});

const ChartContainer = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  '& .MuiTypography-h6': {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
}));

// Location Map Card
const MapCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 12,
  overflow: 'hidden',
}));

const MapContainerWrapper = styled(Box)(({ theme }) => ({
  height: 400,
  [theme.breakpoints.up('md')]: {
    height: 500, // Orta boyutlu ekranlarda (tablet)
  },
  [theme.breakpoints.up('lg')]: {
    height: 600, // Büyük ekranlarda
  },
  [theme.breakpoints.up('xl')]: {
    height: 700, // Çok büyük ekranlarda
  },
  width: '100%',
  '& .leaflet-container': {
    height: '100%',
    width: '100%',
    borderRadius: '0 0 12px 12px',
  }
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  alignItems: 'flex-end'
}));

const ALL_WIDGETS: Widget[] = [
  { 
    id: 'normalTaskStatusChart', 
    title: 'Normal Görev Durum Dağılımı', 
    description: 'Normal (tek seferlik) görevlerin durumlarını gösteren pasta grafik.' 
  },
  { 
    id: 'taskStatusChart', 
    title: 'Günlük Görev Durumu Dağılımı', 
    description: 'Günlük tekrarlayan görevlerin durumlarını (tamamlanan, bekleyen, kaçırılan) gösteren bir pasta grafiktir.' 
  },
  { 
    id: 'weeklyTaskStatusChart', 
    title: 'Haftalık Görev Durumu Dağılımı', 
    description: 'Haftalık tekrarlayan görevlerin durumlarını gösterir.' 
  },
  { 
    id: 'monthlyTaskStatusChart', 
    title: 'Aylık Görev Durumu Dağılımı', 
    description: 'Aylık tekrarlayan görevlerin durumlarını gösterir.' 
  },
  { 
    id: 'personnelPerformanceChart', 
    title: 'Personel Performansı (Günlük)', 
    description: 'Personelin günlük görev tamamlama performansını gösterir.' 
  },
  { 
    id: 'weeklyPersonnelPerformanceChart', 
    title: 'Personel Performansı (Haftalık)', 
    description: 'Personelin haftalık görev tamamlama performansını gösterir.' 
  },
  { 
    id: 'monthlyPersonnelPerformanceChart', 
    title: 'Personel Performansı (Aylık)', 
    description: 'Personelin aylık görev tamamlama performansını gösterir.' 
  },
  { 
    id: 'normalTaskPersonnelPerformance', 
    title: 'Personel Performansı (Normal)', 
    description: 'Personelin normal (tek seferlik) görevlerdeki performansını gösterir.' 
  },
  { 
    id: 'surveyCharts', 
    title: 'Anket Grafikleri', 
    description: 'Genel anket memnuniyet oranı ve son 1 aylık memnuniyet trendini gösterir.' 
  },
  
  { 
    id: 'missedTasks', 
    title: 'Geciken Görevler', 
    description: 'Geciken son 10 görevi (tekrarlayan) gösterir.' 
  },
  { 
    id: 'completedTasks', 
    title: 'Tamamlanan Görevler', 
    description: 'Tamamlanan son 10 görevi (tekrarlayan) gösterir.' 
  },
  { 
    id: 'missedNormalTasks', 
    title: 'Geciken Normal Görevler', 
    description: 'Geciken son 10 normal (tek seferlik) görevi gösterir.' 
  },
  { 
    id: 'completedNormalTasks', 
    title: 'Tamamlanan Normal Görevler', 
    description: 'Tamamlanan son 10 normal (tek seferlik) görevi gösterir.' 
  },
  { 
    id: 'mapView', 
    title: 'Harita Görünümü', 
    description: 'Personelin anlık konumlarını harita üzerinde gösterir.' 
  },
  // İhtiyaç duyulursa daha fazla widget eklenebilir.
];

const Dashboard: React.FC<DashboardProps> = ({ branchId, isManager = false }) => {
  console.time('Dashboard-Total'); // Toplam yüklenme süresini ölçmek için
  console.log('Dashboard yüklenmesi başladı:', new Date().toISOString());
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalPersonnel: 0,
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    weeklyPendingTasks: 0,
    weeklyCompletedTasks: 0,
    monthlyPendingTasks: 0,
    monthlyCompletedTasks: 0,
  });
  const [personnel, setPersonnel] = useState<any[]>([]);
  const [recentTasks, setRecentTasks] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]); // Tüm görevler için liste
  const [taskStatusData, setTaskStatusData] = useState<any[]>([]);
  const [weeklyTaskStatusData, setWeeklyTaskStatusData] = useState<any[]>([]);
  const [monthlyTaskStatusData, setMonthlyTaskStatusData] = useState<any[]>([]);
  const [personnelPerformanceData, setPersonnelPerformanceData] = useState<any[]>([]);
  const [weeklyPersonnelPerformanceData, setWeeklyPersonnelPerformanceData] = useState<any[]>([]);
  const [monthlyPersonnelPerformanceData, setMonthlyPersonnelPerformanceData] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [recentMissedTasks, setRecentMissedTasks] = useState<any[]>([]);
  const [recentCompletedTasks, setRecentCompletedTasks] = useState<any[]>([]);
  const [recentMissedTasksNormal, setRecentMissedTasksNormal] = useState<any[]>([]);
  const [recentCompletedTasksNormal, setRecentCompletedTasksNormal] = useState<any[]>([]);
  const [allMissedTasks, setAllMissedTasks] = useState<any[]>([]);
  const [allCompletedTasks, setAllCompletedTasks] = useState<any[]>([]);
  const [allMissedTasksNormal, setAllMissedTasksNormal] = useState<any[]>([]);
  const [allCompletedTasksNormal, setAllCompletedTasksNormal] = useState<any[]>([]);
  const [allPendingTasks, setAllPendingTasks] = useState<any[]>([]);
  const [missedTasksModalOpen, setMissedTasksModalOpen] = useState(false);
  const [completedTasksModalOpen, setCompletedTasksModalOpen] = useState(false);
  const [missedTasksNormalModalOpen, setMissedTasksNormalModalOpen] = useState(false);
  const [completedTasksNormalModalOpen, setCompletedTasksNormalModalOpen] = useState(false);
  const [pendingTasksModalOpen, setPendingTasksModalOpen] = useState(false);
  const [allCompletedTasksModalOpen, setAllCompletedTasksModalOpen] = useState(false);
  const [worstPerformers, setWorstPerformers] = useState<any[]>([]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('common2');
  
  // Arama için state'ler
  const [personnelSearchTerm, setPersonnelSearchTerm] = useState('');
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  
  // New state for map features
  const [taskLocations, setTaskLocations] = useState<any[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<any[]>([]);
  const [selectedTaskCount, setSelectedTaskCount] = useState<number>(10);
  const [selectedPersonnel, setSelectedPersonnel] = useState<string[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [allTasksWithLocation, setAllTasksWithLocation] = useState<any[]>([]);
  
  // selectedWidgets için localStorage'dan başlangıç değerini alma işlevi
  const getInitialSelectedWidgets = (): string[] => {
    // Eğer localStorage'da kaydedilmiş bir liste varsa, onu kullan
    const savedWidgets = localStorage.getItem('selectedDashboardWidgets');
    
    if (savedWidgets) {
      try {
        return JSON.parse(savedWidgets);
      } catch (error) {
        console.error('LocalStorage widget listesi ayrıştırılamadı:', error);
      }
    }
    
    // Varsayılan widget listesi
    return [
      'statsDaily',
      'statsWeeklyMonthly',
      'taskStatus',
      'weeklyTaskStatus',
      'monthlyTaskStatus',
      'personnelPerformance',
      'weeklyPersonnelPerformance',
      'monthlyPersonnelPerformance',
      'normalTaskPersonnelPerformance',
      'normalTaskStatusChart',
      'surveyCharts',
      'missedTasks',
      'completedTasks',
      'missedTasksNormal',
      'completedTasksNormal',
      'worstPerformers',
      'bestPerformers'
    ];
  };
  
  // Widget seçimi için state'ler
  const [widgetSelectorOpen, setWidgetSelectorOpen] = useState(false);
  const [selectedWidgets, setSelectedWidgets] = useState<string[]>(getInitialSelectedWidgets());
  
  // Widget seçimini değiştir ve localStorage'a kaydet
  const handleWidgetSelectionChange = (widgetId: string) => {
    const newSelectedWidgets = selectedWidgets.includes(widgetId)
      ? selectedWidgets.filter(id => id !== widgetId)
      : [...selectedWidgets, widgetId];
    
    setSelectedWidgets(newSelectedWidgets);
    
    // Seçilen widget'ları localStorage'a kaydet
    localStorage.setItem('selectedDashboardWidgets', JSON.stringify(newSelectedWidgets));
    
    // Veri yükleme işlemi selectedWidgets dependency'si olan useEffect tarafından otomatik tetiklenecek
    console.log("Widget seçimi değişti:", widgetId, selectedWidgets.includes(widgetId) ? "Kapatıldı" : "Açıldı");
  };
  
  // Modal pencereleri için işlevler
  const handleOpenMissedTasksModal = () => {
    setMissedTasksModalOpen(true);
  };
  
  const handleCloseMissedTasksModal = () => {
    setMissedTasksModalOpen(false);
  };
  
  const handleOpenCompletedTasksModal = () => {
    setCompletedTasksModalOpen(true);
  };
  
  const handleCloseCompletedTasksModal = () => {
    setCompletedTasksModalOpen(false);
  };
  
  const handleOpenMissedTasksNormalModal = () => {
    setMissedTasksNormalModalOpen(true);
  };
  
  const handleCloseMissedTasksNormalModal = () => {
    setMissedTasksNormalModalOpen(false);
  };
  
  const handleOpenCompletedTasksNormalModal = () => {
    setCompletedTasksNormalModalOpen(true);
  };
  
  const handleCloseCompletedTasksNormalModal = () => {
    setCompletedTasksNormalModalOpen(false);
  };
  
  const handleOpenPendingTasksModal = () => {
    setPendingTasksModalOpen(true);
  };
  
  const handleClosePendingTasksModal = () => {
    setPendingTasksModalOpen(false);
  };
  
  const handleOpenAllCompletedTasksModal = () => {
    setAllCompletedTasksModalOpen(true);
  };

  const handleCloseAllCompletedTasksModal = () => {
    setAllCompletedTasksModalOpen(false);
  };
  
  // Sayfa yönlendirme fonksiyonları
  const handleNavigateToPersonnel = () => {
    navigate('/personnel');
  };
  
  const handleNavigateToTasks = () => {
    navigate('/tasks');
  };
  
  // Haftalık görevlere yönlendirme
  const handleNavigateToWeeklyTasks = () => {
    navigate('/tasks', { state: { activeTab: 'weekly' } });
  };

  // Aylık görevlere yönlendirme
  const handleNavigateToMonthlyTasks = () => {
    navigate('/tasks', { state: { activeTab: 'monthly' } });
  };
  
  // Veri güncellemesini işler
  const processData = (personnelData: any, tasksData: any, companyData: any = {}) => {
    console.time('processData');
    const processStart = performance.now();
    console.log('processData başladı');
    
    // Personel listesi
    const personnelListStart = performance.now();
    let personnelList = personnelData ? Object.entries(personnelData).map(([id, data]: [string, any]) => ({
      id,
      ...data,
    })) : [];
    
    // Eğer yönetici ve branchId varsa, sadece bu şubeye ait personeli filtrele
    if (isManager && branchId) {
      personnelList = personnelList.filter(person => person.branchesId === branchId);
    }
    
    // Silinen personelleri filtrele - sadece aktif personelleri al
    personnelList = personnelList.filter(person => !person.isDeleted);
    
    setPersonnel(personnelList);
    console.log(`Personel listesi oluşturma süresi: ${performance.now() - personnelListStart}ms`);
    
    // Görev listesi - ana görevleri al (toplam görev sayısı için)
    const tasksListStart = performance.now();
    let tasksList = tasksData ? Object.entries(tasksData)
      .filter(([key]) => key !== 'missedTasks') // missedTasks anahtarını filtrele
      .map(([id, data]: [string, any]) => ({
        id,
        ...data,
      })) : [];
    
    // Eğer yönetici ve branchId varsa, sadece bu şubeye ait görevleri filtrele
    if (isManager && branchId) {
      tasksList = tasksList.filter(task => {
        // Görevin bağlı olduğu personel bu şubede mi kontrol et
        const personnelOfTask = personnelList.find(p => p.id === task.personnelId);
        return personnelOfTask && personnelOfTask.branchesId === branchId;
      });
    }
    
    // Tüm görevleri sakla
    setTasks(tasksList);
    console.log(`Görev listesi oluşturma süresi: ${performance.now() - tasksListStart}ms`);
    
    // Son eklenen görevler
    const sortedTasks = [...tasksList].sort((a, b) => b.createdAt - a.createdAt).slice(0, 2);
    setRecentTasks(sortedTasks);
    
    // Devam eden görevleri al (accepted ve assigned olanlar)
    const pendingTasksList = tasksList
      .filter(task => task.status === 'accepted' || task.status === 'assigned')
      .map(task => ({
        id: task.id,
        name: task.name,
        description: task.description || '',
        status: task.status,
        personnelId: task.personnelId,
        personnelName: personnelData[task.personnelId]?.name || 'Atanmamış',
      }));
    setAllPendingTasks(pendingTasksList);
    
    // İstatistikleri hesapla
    const totalTasks = tasksList.filter(task => task.status !== 'completed').length;
    const completedTasksCount = tasksList.filter(task => task.status === 'completed').length;
    const activeTasksCount = tasksList.filter(task => task.status === 'accepted' || task.status === 'assigned').length;
    const waitingTasksCount = tasksList.filter(task => task.status === 'waiting').length;
    const pendingTasksCount = tasksList.filter(task => task.status === 'pending').length;
    
    // Task ID ile görev objesini bulmak için yardımcı fonksiyon
    const getTaskById = (taskId: string) => {
      return tasksList.find(t => t.id === taskId);
    };

    // Tamamlanmamış (missed) görevleri hesapla
    let missedTasksCount = 0;
    
    // Missed task listesi - tekrarlı ve normal olarak ayır
    const missedTasksList: any[] = [];
    const missedTasksListNormal: any[] = [];
    
    // Tamamlanmış görev listesi - tekrarlı ve normal olarak ayır
    const completedTasksList: any[] = [];
    const completedTasksListNormal: any[] = [];
    
    // Tamamlanmış normal görevleri önce zamanında ve gecikmiş olarak ayır
    const onTimeNormalTasksList: any[] = [];  // Zamanında tamamlanan normal görevler
    const lateNormalTasksList: any[] = [];    // Geç tamamlanan normal görevler
    
    // Personel performans değerlendirmesi için
    const personnelMissedCounts: { [key: string]: { id: string, name: string, missedCount: number } } = {};
    
    // Tüm personeli başlangıçta 0 gecikme ile ekle
    personnelList.forEach(person => {
      personnelMissedCounts[person.id] = {
        id: person.id,
        name: person.name,
        missedCount: 0
      };
    });
    
    // Normal tamamlanmış görevleri (task altında olup status=completed olanlar) kontrol et
    const normalCompletedTasks = tasksList.filter(task => 
      task.status === 'completed' && 
      !task.isRecurring && 
      task.completedAt && 
      task.deadline
    );
    
    // Normal tamamlanmış görevleri zamanında ve gecikmeli olarak ayır
    normalCompletedTasks.forEach(task => {
      // Personel bilgisini bul
      const personnelName = personnelData[task.personnelId]?.name || 'Atanmamış';
      
      // Bitirilme ve görev tarihini date objelerine dönüştür
      let completionDate = null;
      let deadlineDate = null;
      
      // completedAt bir timestamp olabilir veya tarih string'i olabilir
      if (typeof task.completedAt === 'number') {
        completionDate = new Date(task.completedAt);
      } else if (typeof task.completedAt === 'string') {
        completionDate = new Date(task.completedAt);
      }
      
      // deadline bir string olarak saklanmış olabilir
      if (typeof task.deadline === 'string') {
        deadlineDate = new Date(task.deadline);
      }
      
      // Eğer tarihler düzgün şekilde çevrilebildiyse
      if (completionDate && deadlineDate && !isNaN(completionDate.getTime()) && !isNaN(deadlineDate.getTime())) {
        // Görevi hazırla
        const completedNormalTask = {
          id: task.id,
          name: task.name,
          description: task.description || '',
          // Date'leri okunabilir formatta sakla
          date: deadlineDate.toLocaleDateString('tr-TR'),
          time: deadlineDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          completedDate: completionDate.toLocaleDateString('tr-TR'),
          completedTime: completionDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          completedAt: task.completedAt,
          deadline: task.deadline,
          personnelId: task.personnelId,
          personnelName: personnelName,
          isRecurring: false
        };
        
        // Zamanında mı geç mi tamamlanmış kontrol et
        if (completionDate > deadlineDate) {
          // Geç tamamlanan görev
          lateNormalTasksList.push(completedNormalTask);
        } else {
          // Zamanında tamamlanan görev
          onTimeNormalTasksList.push(completedNormalTask);
        }
      }
    });
    
    // Geç tamamlanan normal görevleri missedTasksListNormal'e ekle
    missedTasksListNormal.push(...lateNormalTasksList);
    
    // Zamanında tamamlanan normal görevleri completedTasksListNormal'e ekle
    completedTasksListNormal.push(...onTimeNormalTasksList);
    
    // Eğer şirketin missedTasks verisi varsa say
    if (companyData && companyData.missedTasks) {
      console.log("missedTasks bulundu:", companyData.missedTasks);
      
      // missedTasks yapısını incele ve say
      Object.entries(companyData.missedTasks).forEach(([taskId, taskDates]: [string, any]) => {
        // Her tarih için
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          // Her saat için
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            // İlgili görevi tasks listesinden bul (atanan personeli almak için)
            const task = getTaskById(taskId);
            
            // Personel bilgisini bul
            let personnelName = "Atanmamış";
            let personnelId = null;
            if (task && task.personnelId) {
              personnelId = task.personnelId;
              const person = personnelList.find(p => p.id === task.personnelId);
              
              // Eğer bu personel yöneticinin şubesine aitse sayıma dahil et
              if (person) {
                // Eğer yönetici ve branchId varsa, sadece bu şubeye ait missed görevleri say
                if (!isManager || !branchId || person.branchesId === branchId) {
                  missedTasksCount++;
                  personnelName = person.name;
                  
                  // Personelin gecikme sayısını artır
                  if (personnelMissedCounts[person.id]) {
                    personnelMissedCounts[person.id].missedCount += 1;
                  }
                  
                  // Görev nesnesini hazırla
                  const missedTaskItem = {
                    id: `${taskId}-${date}-${time}`,
                    name: taskData.taskName,
                    description: taskData.taskDescription || '',
                    date: date,
                    time: time,
                    missedAt: taskData.missedAt,
                    personnelId: personnelId,
                    personnelName: personnelName,
                    isRecurring: task?.isRecurring || false
                  };
                  
                  // Tekrarlı veya normal görev olarak ayır
                  if (missedTaskItem.isRecurring) {
                    missedTasksList.push(missedTaskItem);
                  } else {
                    missedTasksListNormal.push(missedTaskItem);
                  }
                }
              }
            }
          });
        });
      });
      
      console.log("Toplam missed görev sayısı:", missedTasksCount);
    }
    
    // Tamamlanan görevleri al
    let completedTasksFromDbCount = 0;
    if (companyData && companyData.completedTasks) {
      Object.entries(companyData.completedTasks).forEach(([taskId, taskDates]: [string, any]) => {
        // Her tarih için
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          // Her saat için
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            // İlgili görevi tasks listesinden bul (atanan personeli almak için)
            const task = getTaskById(taskId);
            
            // Personel bilgisini bul
            let personnelName = "Atanmamış";
            let personnelId = taskData.completedBy || task?.personnelId || null;
            
            // Öncelikle startedBy veya completedBy bilgisini kullan
            if (personnelId) {
              const person = personnelList.find(p => p.id === personnelId);
              
              // Eğer bu personel yöneticinin şubesine aitse sayıma dahil et
              if (person) {
                // Eğer yönetici ve branchId varsa, sadece bu şubeye ait tamamlanan görevleri say
                if (!isManager || !branchId || person.branchesId === branchId) {
                  completedTasksFromDbCount++; // Tamamlanan görev sayacını artır
                  personnelName = person.name;
                  
                  // Görev nesnesini hazırla
                  const completedTaskItem = {
                    id: `${taskId}-${date}-${time}`,
                    name: taskData.taskName,
                    description: taskData.taskDescription || '',
                    date: date,
                    time: time,
                    completedAt: taskData.completedAt,
                    personnelId: personnelId,
                    personnelName: personnelName,
                    isRecurring: task?.isRecurring || false
                  };
                  
                  // Tekrarlı veya normal görev olarak ayır
                  if (completedTaskItem.isRecurring) {
                    completedTasksList.push(completedTaskItem);
                  } else {
                    completedTasksListNormal.push(completedTaskItem);
                  }
                }
              }
            }
          });
        });
      });
    }
    
    // Toplam tamamlanan görev sayısı: ana liste + veritabanından gelen
    const totalCompletedTasks = completedTasksCount + completedTasksFromDbCount;
    console.log("Toplam tamamlanan görevler:", totalCompletedTasks);
    console.log("- Ana listeden:", completedTasksCount);
    console.log("- Veritabanından:", completedTasksFromDbCount);
    
    // En kötü performans gösteren personelleri hesapla (en çok görev geciktiren)
    const worstPerformers = Object.values(personnelMissedCounts)
      .filter(person => person.missedCount > 0) // Sadece görevi geciktirmiş personelleri al
      .sort((a, b) => b.missedCount - a.missedCount) // Gecikme sayısına göre sırala (çoktan aza)
      .slice(0, 10); // En kötü 10 personeli al
    
    setWorstPerformers(worstPerformers);
    
    // Stats state'ini güncelle (haftalık ve aylık görev sayılarını ekliyoruz)
    setStats({
      totalPersonnel: personnelList.length,
      totalTasks, // Sadece tasks altındaki görevleri göster, missedTasks'ı dahil etme
      completedTasks: totalCompletedTasks, // Değiştirildi: Toplam tamamlanan görev sayısı
      pendingTasks: activeTasksCount,
      weeklyPendingTasks: companyData?.weeklyTasks ? Object.keys(companyData.weeklyTasks).length : 0,
      weeklyCompletedTasks: companyData?.completedWeeklyTasks ? Object.keys(companyData.completedWeeklyTasks).length : 0,
      monthlyPendingTasks: companyData?.monthlyTasks ? Object.keys(companyData.monthlyTasks).length : 0,
      monthlyCompletedTasks: companyData?.completedMonthlyTasks ? Object.keys(companyData.completedMonthlyTasks).length : 0,
    });
    
    console.log("Normal görev sayıları:", {
      gecikmiş: lateNormalTasksList.length,
      zamanındaTamamlanan: onTimeNormalTasksList.length
    });
    
    // Son 10 geciken görevi tarih ve saate göre sırala - Tekrarlı ve normal olarak ayrı ayrı
    const sortedMissedTasks = [...missedTasksList]
      .sort((a, b) => b.missedAt - a.missedAt)
      .slice(0, 10);
      
    // Son 10 tamamlanan görevi tarih ve saate göre sırala - Tekrarlı ve normal olarak ayrı ayrı
    const sortedCompletedTasks = [...completedTasksList]
      .sort((a, b) => b.completedAt - a.completedAt)
      .slice(0, 10);
      
    // Normal görevler için sıralama
    const sortedMissedTasksNormal = [...missedTasksListNormal]
      .sort((a, b) => {
        // Eğer her ikisi de missedAt değerine sahipse ona göre sırala
        if (a.missedAt && b.missedAt) {
          return b.missedAt - a.missedAt;
        }
        // Eğer completedAt değerine sahipse ona göre sırala (geç tamamlanan normal görevler için)
        else if (a.completedAt && b.completedAt) {
          return b.completedAt - a.completedAt;
        }
        // Diğer durumlarda mevcut sırayı koru
        return 0;
      })
      .slice(0, 10);
      
    const sortedCompletedTasksNormal = [...completedTasksListNormal]
      .sort((a, b) => {
        // Eğer her ikisi de completedAt değerine sahipse ona göre sırala
        if (a.completedAt && b.completedAt) {
          return b.completedAt - a.completedAt;
        }
        // Diğer durumlarda mevcut sırayı koru
        return 0;
      })
      .slice(0, 10);
    
    setRecentMissedTasks(sortedMissedTasks);
    setRecentCompletedTasks(sortedCompletedTasks);
    setRecentMissedTasksNormal(sortedMissedTasksNormal);
    setRecentCompletedTasksNormal(sortedCompletedTasksNormal);

    // Tüm geciken ve tamamlanan görevleri de sakla (en yeniden eskiye doğru sıralı)
    setAllMissedTasks([...missedTasksList].sort((a, b) => b.missedAt - a.missedAt));
    setAllCompletedTasks([...completedTasksList].sort((a, b) => b.completedAt - a.completedAt));
    setAllMissedTasksNormal([...missedTasksListNormal].sort((a, b) => {
      // Eğer her ikisi de missedAt değerine sahipse ona göre sırala
      if (a.missedAt && b.missedAt) {
        return b.missedAt - a.missedAt;
      }
      // Eğer completedAt değerine sahipse ona göre sırala (geç tamamlanan normal görevler için)
      else if (a.completedAt && b.completedAt) {
        return b.completedAt - a.completedAt;
      }
      // Diğer durumlarda mevcut sırayı koru
      return 0;
    }));
    setAllCompletedTasksNormal([...completedTasksListNormal].sort((a, b) => {
      // Eğer her ikisi de completedAt değerine sahipse ona göre sırala
      if (a.completedAt && b.completedAt) {
        return b.completedAt - a.completedAt;
      }
      // Diğer durumlarda mevcut sırayı koru
      return 0;
    }));
    
    // Görev durumu verilerini hazırla
    setTaskStatusData([
      { name: 'completed', value: totalCompletedTasks, color: THEME_COLORS.completed },
      { name: 'inProgress', value: activeTasksCount, color: '#1976D2' },
      { name: 'pending', value: waitingTasksCount, color: '#2196F3' },
      { name: 'onHold', value: pendingTasksCount, color: THEME_COLORS.pending },
      { name: 'incomplete', value: missedTasksCount, color: '#F44336' },
    ].filter(item => item.value > 0));
    
    // Haftalık görev durumu verilerini hazırla
    // Şubeye göre haftalık kaçırılan görev sayısı hesapla
    const filteredWeeklyMissedTasksCount = companyData?.missedWeeklyTasks ? 
      Object.entries(companyData.missedWeeklyTasks).filter(([taskId, taskData]) => {
        // Şube filtresi
        if (isManager && branchId) {
          // Görev ID'sini kullanarak ilgili görevi bul
          let personnelId: string | null = null;
          
          // Önce companyData.weeklyTasks'te kontrol et
          if (companyData.weeklyTasks && companyData.weeklyTasks[taskId]) {
            personnelId = companyData.weeklyTasks[taskId].personnelId;
          }
          
          if (personnelId) {
            const person = personnelList.find(p => p.id === personnelId);
            if (!person || person.branchesId !== branchId) {
              return false; // Bu görev bu şubeye ait değil, atlayalım
            }
          }
        }
        return true;
      }).length : 0;
    
    // Şubeye göre haftalık tamamlanan görev sayısı
    const filteredWeeklyCompletedTasksCount = companyData?.completedWeeklyTasks ? 
      Object.entries(companyData.completedWeeklyTasks).filter(([taskId, taskDates]) => {
        // Şube filtresi
        if (isManager && branchId) {
          // İlk önce personel ID'sini bulmak için tamamlanmış görev verilerini kontrol et
          let belongsToBranch = false;
          
          // Eğer görevi şubeyle eşleşen personel tamamladıysa, kabul et
          for (const [date, timeSlots] of Object.entries(taskDates as Record<string, any>)) {
            for (const [time, taskData] of Object.entries(timeSlots as any)) {
              const personnelId = (taskData as any).personnelId || (taskData as any).completedBy;
              if (personnelId) {
                const person = personnelList.find(p => p.id === personnelId);
                if (person && person.branchesId === branchId) {
                  belongsToBranch = true;
                  break;
                }
              }
            }
            if (belongsToBranch) break;
          }
          
          // Personel şubeye ait değilse, bu görevi sayma
          if (!belongsToBranch) return false;
        }
        return true;
      }).length : 0;
    
    // Şubeye göre haftalık devam eden görev sayısı
    const filteredWeeklyPendingTasksCount = companyData?.weeklyTasks ? 
      Object.entries(companyData.weeklyTasks).filter(([taskId, taskData]) => {
        // Şube filtresi
        if (isManager && branchId) {
          const personnelId = (taskData as any).personnelId;
          if (personnelId) {
            const person = personnelList.find(p => p.id === personnelId);
            if (!person || person.branchesId !== branchId) {
              return false; // Bu görev bu şubeye ait değil, atlayalım
            }
          }
        }
        return true;
      }).length : 0;
    
    setWeeklyTaskStatusData([
      { name: 'Tamamlanan', value: filteredWeeklyCompletedTasksCount, color: THEME_COLORS.completed },
      { name: 'Devam Eden', value: filteredWeeklyPendingTasksCount, color: '#1976D2' },
      { name: 'Tamamlanmamış', value: filteredWeeklyMissedTasksCount, color: '#F44336' },
    ].filter(item => item.value > 0));
    
    // Aylık görev durumu verilerini pasta grafik için hazırla
    // Şubeye göre aylık kaçırılan görev sayısı
    const filteredMonthlyMissedTasksCount = companyData?.missedMonthlyTasks ? 
      Object.entries(companyData.missedMonthlyTasks).filter(([taskId, taskData]) => {
        // Şube filtresi
        if (isManager && branchId) {
          // Görev ID'sini kullanarak ilgili görevi bul
          let personnelId: string | null = null;
          
          // Önce companyData.monthlyTasks'te kontrol et
          if (companyData.monthlyTasks && companyData.monthlyTasks[taskId]) {
            personnelId = companyData.monthlyTasks[taskId].personnelId;
          }
          
          if (personnelId) {
            const person = personnelList.find(p => p.id === personnelId);
            if (!person || person.branchesId !== branchId) {
              return false; // Bu görev bu şubeye ait değil, atlayalım
            }
          }
        }
        return true;
      }).length : 0;
    
    // Şubeye göre aylık tamamlanan görev sayısı
    const filteredMonthlyCompletedTasksCount = companyData?.completedMonthlyTasks ? 
      Object.entries(companyData.completedMonthlyTasks).filter(([taskId, taskDates]) => {
        // Şube filtresi
        if (isManager && branchId) {
          // İlk önce personel ID'sini bulmak için tamamlanmış görev verilerini kontrol et
          let belongsToBranch = false;
          
          // Eğer görevi şubeyle eşleşen personel tamamladıysa, kabul et
          for (const [date, timeSlots] of Object.entries(taskDates as Record<string, any>)) {
            for (const [time, taskData] of Object.entries(timeSlots as any)) {
              const personnelId = (taskData as any).personnelId || (taskData as any).completedBy;
              if (personnelId) {
                const person = personnelList.find(p => p.id === personnelId);
                if (person && person.branchesId === branchId) {
                  belongsToBranch = true;
                  break;
                }
              }
            }
            if (belongsToBranch) break;
          }
          
          // Personel şubeye ait değilse, bu görevi sayma
          if (!belongsToBranch) return false;
        }
        return true;
      }).length : 0;
    
    // Şubeye göre aylık devam eden görev sayısı
    const filteredMonthlyPendingTasksCount = companyData?.monthlyTasks ? 
      Object.entries(companyData.monthlyTasks).filter(([taskId, taskData]) => {
        // Şube filtresi
        if (isManager && branchId) {
          const personnelId = (taskData as any).personnelId;
          if (personnelId) {
            const person = personnelList.find(p => p.id === personnelId);
            if (!person || person.branchesId !== branchId) {
              return false; // Bu görev bu şubeye ait değil, atlayalım
            }
          }
        }
        return true;
      }).length : 0;
    
    setMonthlyTaskStatusData([
      { name: 'Tamamlanan', value: filteredMonthlyCompletedTasksCount, color: THEME_COLORS.completed },
      { name: 'Devam Eden', value: filteredMonthlyPendingTasksCount, color: '#1976D2' },
      { name: 'Tamamlanmamış', value: filteredMonthlyMissedTasksCount, color: '#F44336' },
    ].filter(item => item.value > 0));
    
    // Stats state'ini şube filtresine göre güncelle
    setStats({
      totalPersonnel: personnelList.length,
      totalTasks, // Sadece tasks altındaki görevleri göster, missedTasks'ı dahil etme
      completedTasks: totalCompletedTasks, // Değiştirildi: Toplam tamamlanan görev sayısı
      pendingTasks: activeTasksCount,
      weeklyPendingTasks: filteredWeeklyPendingTasksCount,
      weeklyCompletedTasks: filteredWeeklyCompletedTasksCount,
      monthlyPendingTasks: filteredMonthlyPendingTasksCount,
      monthlyCompletedTasks: filteredMonthlyCompletedTasksCount,
    });
    
    // Personel performans verilerini hazırla
    // Personellerin tamamladığı görev sayılarını hesapla
    const personnelPerformance: { [key: string]: { name: string, completed: number, pending: number, missed: number } } = {};
    
    personnelList.forEach(person => {
      personnelPerformance[person.id] = {
        name: person.name,
        completed: 0,   // Tüm tamamlanan görevler (ana liste + veritabanı)
        pending: 0,     // Devam eden görevler
        missed: 0       // Kaçırılan görevler
      };
    });
    
    // Ana görev listesindeki tamamlanan ve devam eden görevleri hesapla
    tasksList.forEach(task => {
      if (task.personnelId && personnelPerformance[task.personnelId]) {
        if (task.status === 'completed') {
          personnelPerformance[task.personnelId].completed += 1;
        } else if (task.status === 'accepted' || task.status === 'assigned') {
          personnelPerformance[task.personnelId].pending += 1;
        }
      }
    });
    
    // Veritabanından gelen tamamlanan görevleri hesapla
    if (companyData && companyData.completedTasks) {
      Object.entries(companyData.completedTasks).forEach(([taskId, taskDates]: [string, any]) => {
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            // Personel bilgisini bul
            let personnelId = null;
            
            // Öncelikle completedBy varsa kullan
            if (taskData.completedBy) {
              personnelId = taskData.completedBy;
            } 
            // Yoksa ilgili görevi bul ve onun personnelId'sini kullan
            else {
              const task = tasksList.find(t => t.id === taskId);
              if (task && task.personnelId) {
                personnelId = task.personnelId;
              }
            }
            
            // Personel bulunabildiyse ve dizinde varsa sayacı artır
            if (personnelId && personnelPerformance[personnelId]) {
              personnelPerformance[personnelId].completed += 1;
            }
          });
        });
      });
    }
    
    // Kaçırılan görevleri hesapla
    if (companyData && companyData.missedTasks) {
      Object.entries(companyData.missedTasks).forEach(([taskId, taskDates]: [string, any]) => {
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            // İlgili görevi tasks listesinden bul (atanan personeli almak için)
            const task = tasksList.find(t => t.id === taskId);
            
            // Personel bilgisini bul
            if (task && task.personnelId && personnelPerformance[task.personnelId]) {
              personnelPerformance[task.personnelId].missed += 1;
            }
          });
        });
      });
    }
    
    // Performans verilerini grafiğe uygun hale getir
    const performanceData = Object.values(personnelPerformance)
      .filter(p => p.completed > 0 || p.pending > 0 || p.missed > 0) // Görevi olan personelleri göster
      .sort((a, b) => {
        // Toplam görev sayısına göre sırala (tüm kategoriler)
        const totalA = a.completed + a.pending + a.missed;
        const totalB = b.completed + b.pending + b.missed;
        return totalB - totalA;
      })
      .slice(0, 5); // İlk 5 personeli al
    
    setPersonnelPerformanceData(performanceData);

    // Extract all completed tasks with location data
    const tasksWithLocation: any[] = [];
    
    if (companyData && companyData.completedTasks) {
      Object.entries(companyData.completedTasks).forEach(([taskId, taskDates]: [string, any]) => {
        // Her tarih için
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          // Her saat için
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            // Check if task has location data
            if (taskData.completionLocation) {
              // Find task and personnel info
              const task = tasksList.find(t => t.id === taskId);
              let personnelName = "Atanmamış";
              let personnelId = null;
              
              // Determine who completed the task
              if (taskData.completedBy) {
                personnelId = taskData.completedBy;
                const person = personnelList.find(p => p.id === taskData.completedBy);
                if (person) personnelName = person.name;
              } else if (task && task.personnelId) {
                personnelId = task.personnelId;
                const person = personnelList.find(p => p.id === task.personnelId);
                if (person) personnelName = person.name;
              }
              
              // Add to location list
              tasksWithLocation.push({
                id: `${taskId}-${date}-${time}`,
                taskId: taskId,
                name: taskData.taskName,
                description: taskData.taskDescription || '',
                date: date,
                time: time,
                location: taskData.completionLocation,
                completedAt: taskData.completedAt,
                personnelId: personnelId,
                personnelName: personnelName
              });
            }
          });
        });
      });
    }
    
    // Sort by completion time (newest first)
    tasksWithLocation.sort((a, b) => b.completedAt - a.completedAt);
    
    // Set all tasks with location data
    setAllTasksWithLocation(tasksWithLocation);
    
    // Set initial filtered tasks (most recent N tasks)
    setFilteredLocations(tasksWithLocation.slice(0, selectedTaskCount));
    
    // Haftalık görev performans verilerini hazırla
    // Personellerin tamamladığı haftalık görev sayılarını hesapla
    const weeklyPersonnelPerformance: { [key: string]: { name: string, completed: number, pending: number, missed: number } } = {};
    
    personnelList.forEach(person => {
      weeklyPersonnelPerformance[person.id] = {
        name: person.name,
        completed: 0,
        pending: 0,
        missed: 0
      };
    });
    
    // Aktif haftalık görevleri hesapla
    if (companyData && companyData.weeklyTasks) {
      Object.entries(companyData.weeklyTasks).forEach(([taskId, taskData]: [string, any]) => {
        if (taskData.personnelId && weeklyPersonnelPerformance[taskData.personnelId]) {
          weeklyPersonnelPerformance[taskData.personnelId].pending += 1;
        }
      });
    }
    
    // Tamamlanan haftalık görevleri hesapla
    if (companyData && companyData.completedWeeklyTasks) {
      Object.entries(companyData.completedWeeklyTasks).forEach(([taskId, taskDates]: [string, any]) => {
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            if (taskData.personnelId && weeklyPersonnelPerformance[taskData.personnelId]) {
              weeklyPersonnelPerformance[taskData.personnelId].completed += 1;
            }
          });
        });
      });
    }
    
    // Kaçırılan haftalık görevleri hesapla
    if (companyData && companyData.missedWeeklyTasks) {
      Object.entries(companyData.missedWeeklyTasks).forEach(([taskId, taskDates]: [string, any]) => {
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            if (taskData.personnelId && weeklyPersonnelPerformance[taskData.personnelId]) {
              weeklyPersonnelPerformance[taskData.personnelId].missed += 1;
            }
          });
        });
      });
    }
    
    // Haftalık performans verilerini grafiğe uygun hale getir
    const weeklyPerformanceData = Object.values(weeklyPersonnelPerformance)
      .filter(p => p.completed > 0 || p.pending > 0 || p.missed > 0) // Görevi olan personelleri göster
      .sort((a, b) => {
        // Toplam görev sayısına göre sırala (tüm kategoriler)
        const totalA = a.completed + a.pending + a.missed;
        const totalB = b.completed + b.pending + b.missed;
        return totalB - totalA;
      })
      .slice(0, 5); // İlk 5 personeli al
    
    setWeeklyPersonnelPerformanceData(weeklyPerformanceData);

    // Aylık personel performans verilerini hazırla
    // Personellerin tamamladığı aylık görev sayılarını hesapla
    const monthlyPersonnelPerformance: { [key: string]: { name: string, completed: number, pending: number, missed: number } } = {};
    
    personnelList.forEach(person => {
      monthlyPersonnelPerformance[person.id] = {
        name: person.name,
        completed: 0,
        pending: 0,
        missed: 0
      };
    });
    
    // Aktif aylık görevleri hesapla
    if (companyData && companyData.monthlyTasks) {
      Object.entries(companyData.monthlyTasks).forEach(([taskId, taskData]: [string, any]) => {
        if (taskData.personnelId && monthlyPersonnelPerformance[taskData.personnelId]) {
          monthlyPersonnelPerformance[taskData.personnelId].pending += 1;
        }
      });
    }
    
    // Tamamlanan aylık görevleri hesapla
    if (companyData && companyData.completedMonthlyTasks) {
      Object.entries(companyData.completedMonthlyTasks).forEach(([taskId, taskDates]: [string, any]) => {
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            if (taskData.personnelId && monthlyPersonnelPerformance[taskData.personnelId]) {
              monthlyPersonnelPerformance[taskData.personnelId].completed += 1;
            }
          });
        });
      });
    }
    
    // Kaçırılan aylık görevleri hesapla
    if (companyData && companyData.missedMonthlyTasks) {
      Object.entries(companyData.missedMonthlyTasks).forEach(([taskId, taskDates]: [string, any]) => {
        Object.entries(taskDates).forEach(([date, timeSlots]: [string, any]) => {
          Object.entries(timeSlots).forEach(([time, taskData]: [string, any]) => {
            if (taskData.personnelId && monthlyPersonnelPerformance[taskData.personnelId]) {
              monthlyPersonnelPerformance[taskData.personnelId].missed += 1;
            }
          });
        });
      });
    }
    
    // Aylık performans verilerini grafiğe uygun hale getir
    const monthlyPerformanceData = Object.values(monthlyPersonnelPerformance)
      .filter(p => p.completed > 0 || p.pending > 0 || p.missed > 0) // Görevi olan personelleri göster
      .sort((a, b) => {
        // Toplam görev sayısına göre sırala (tüm kategoriler)
        const totalA = a.completed + a.pending + a.missed;
        const totalB = b.completed + b.pending + b.missed;
        return totalB - totalA;
      })
      .slice(0, 5); // İlk 5 personeli al
    
    setMonthlyPersonnelPerformanceData(monthlyPerformanceData);
    
    console.log(`processData toplam süresi: ${performance.now() - processStart}ms`);
    console.timeEnd('processData');
  };
  
  // Filter locations based on selections
  useEffect(() => {
    let filtered = [...allTasksWithLocation];
    
    // Filter by personnel if any selected
    if (selectedPersonnel.length > 0) {
      filtered = filtered.filter(task => selectedPersonnel.includes(task.personnelId));
    }
    
    // Filter by task ID if any selected
    if (selectedTasks.length > 0) {
      filtered = filtered.filter(task => selectedTasks.includes(task.taskId));
    }
    
    // Limit to selected count
    filtered = filtered.slice(0, selectedTaskCount);
    
    setFilteredLocations(filtered);
  }, [selectedTaskCount, selectedPersonnel, selectedTasks, allTasksWithLocation]);
  
  // Handle task count selection
  const handleTaskCountChange = (event: SelectChangeEvent<number>) => {
    setSelectedTaskCount(event.target.value as number);
  };
  
  // Handle personnel selection
  const handlePersonnelChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedPersonnel(event.target.value as string[]);
  };
  
  // Handle task selection
  const handleTaskChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedTasks(event.target.value as string[]);
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSelectedTaskCount(10);
    setSelectedPersonnel([]);
    setSelectedTasks([]);
  };
  
  // Dashboard kurulumu - ilk yükleme
  useEffect(() => {
    console.time('Dashboard-Setup');
    console.log('Dashboard setup başladı:', new Date().toISOString());
    
    const setupDashboard = async () => {
      try {
        const setupStart = performance.now();
        console.log('Setup işlemi başladı');
        
        const uid = ownAuthSession.getCurrentUid();
        
        if (!uid) {
          console.error("Kullanıcı girişi bulunamadı!");
          return;
        }
        
        const userFetchStart = performance.now();
        // Kullanıcının firma ID'sini getir (SQL — gts_auth_api)
        const userData = await firebaseDataService.getUserData(uid);
        const companyId = userData?.companyId;
        console.log(`Kullanıcı verisi çekme süresi: ${performance.now() - userFetchStart}ms`);
        
        if (!companyId) {
          console.error("Kullanıcının firma bilgisi bulunamadı!");
          return;
        }
        
        setCompanyId(companyId);
        
        const widgetLoadStart = performance.now();
        // Kaydedilmiş widget seçimlerini yükle
        const savedWidgets = localStorage.getItem('selectedDashboardWidgets');
        if (savedWidgets) {
          try {
            const parsedWidgets = JSON.parse(savedWidgets);
            if (Array.isArray(parsedWidgets)) {
              setSelectedWidgets(parsedWidgets);
            }
          } catch (e) {
            console.error("Widget ayarları yüklenemedi:", e);
          }
        }
        console.log(`Widget ayarları yükleme süresi: ${performance.now() - widgetLoadStart}ms`);
        
        // İlk yüklemede veri çekmeyi burada yapmıyoruz, selectedWidgets dependency'si olan 
        // diğer useEffect bu işi yapacak
        
        console.log(`Toplam setup süresi: ${performance.now() - setupStart}ms`);
      } catch (error) {
        console.error("Dashboard kurulum hatası:", error);
        setError("Veriler yüklenirken bir hata oluştu.");
      }
    };

    if (ownAuthSession.getCurrentUid()) {
      setupDashboard().then(() => {
        console.timeEnd('Dashboard-Setup');
      });
    }
  }, [branchId, isManager]);
  
  // Widget'lara göre veri yükleme işlevi
  const loadDataBasedOnSelectedWidgets = async (companyId: string) => {
    try {
      const loadStart = performance.now();
      console.log('Veri yükleme başladı:', new Date().toISOString());
      console.time('Load-Data-Based-On-Widgets');
      
      setLoading(true);
      
      // İhtiyaç duyduğumuz veri kategorilerini belirleyelim
      const needsPersonnelData = selectedWidgets.some(w => 
        ['statsDaily', 'personnelPerformance', 'weeklyPersonnelPerformance', 
         'monthlyPersonnelPerformance', 'worstPerformers', 'bestPerformers',
         'taskLocations'].includes(w)
      );
      
      const needsTaskData = selectedWidgets.some(w => 
        ['statsDaily', 'statsWeeklyMonthly', 'taskStatus', 'missedTasks', 
         'completedTasks', 'missedTasksNormal', 'completedTasksNormal',
         'pendingTasks', 'allCompletedTasks', 'taskLocations'].includes(w)
      );
      
      const needsMissedTasksData = selectedWidgets.some(w => 
        ['missedTasks', 'worstPerformers', 'taskStatus'].includes(w)
      );
      
      const needsCompletedTasksData = selectedWidgets.some(w => 
        ['completedTasks', 'taskStatus', 'taskLocations'].includes(w)
      );
      
      const needsWeeklyTasksData = selectedWidgets.some(w => 
        ['statsWeeklyMonthly', 'weeklyTaskStatus', 'weeklyPersonnelPerformance'].includes(w)
      );
      
      const needsMonthlyTasksData = selectedWidgets.some(w => 
        ['statsWeeklyMonthly', 'monthlyTaskStatus', 'monthlyPersonnelPerformance'].includes(w)
      );

      // Şubeyi kontrol et
      const branchCheckStart = performance.now();
      let effectiveBranchId = branchId;
      if (!effectiveBranchId && !isManager && ownAuthSession.getCurrentUid()) {
        try {
          const userData = await firebaseDataService.getUserData(ownAuthSession.getCurrentUid() as string);
          effectiveBranchId = userData?.branchesId || null;
        } catch (error) {
          console.error('Kullanıcı verisi alınamadı:', error);
        }
      }
      console.log(`Şube kontrolü süresi: ${performance.now() - branchCheckStart}ms`);
      
      console.log("Dashboard optimized loading:", isManager ? "Yönetici modu" : "Normal mod");
      if (effectiveBranchId) {
        console.log("Şube ID:", effectiveBranchId);
      }
      
      if (selectedWidgets.length === 0) {
        console.log("Hiç widget seçilmemiş, veri çekme işlemi atlanıyor.");
        setLoading(false);
        console.timeEnd('Load-Data-Based-On-Widgets');
        console.timeEnd('Dashboard-Total');
        return;
      }
      
      // Sadece gereken verileri çekelim
      let personnelData = {};
      let tasksData = {};
      let companyData: {
        missedTasks?: any;
        completedTasks?: any;
        weeklyTasks?: any;
        monthlyTasks?: any;
        missedWeeklyTasks?: any;
        missedMonthlyTasks?: any;
        completedWeeklyTasks?: any;
        completedMonthlyTasks?: any;
        [key: string]: any;
      } = {};
      
      // Paralel veri çekme için promise'lar
      const promises = [];
      
      // Şirket verisi artık otomatik çekilmiyor, her veri ihtiyaç duyulduğunda ayrı ayrı çekilecek
      
      if (needsPersonnelData) {
        const personnelFetchStart = performance.now();
        console.log("Personel verileri yükleniyor...");
        const personnelPromise = firebaseDataService.getPersonnel(companyId)
          .then(data => { 
            // Array'i objeye çevir
            if (Array.isArray(data)) {
              personnelData = data.reduce((acc: any, p: any) => { acc[p.id] = p; return acc; }, {});
            } else {
              personnelData = data || {}; 
            }
            console.log(`Personel verisi çekme tamamlandı: ${performance.now() - personnelFetchStart}ms`);
          })
          .catch(err => {
            console.error('Personel verisi alınamadı:', err);
            personnelData = {};
          });
        promises.push(personnelPromise);
      } else {
        console.log("Personel verileri atlandı - gerekli widget seçilmemiş");
      }
      
      // Faz 5: Tüm görev verileri SQL'den (taskDataService) — RTDB şekline uyarlanır.
      // tasks/weekly/monthly tanımları + completed/missed tamamlama kayıtları tek akışta çekilir.
      const anyTaskData = needsTaskData || needsMissedTasksData || needsCompletedTasksData
        || needsWeeklyTasksData || needsMonthlyTasksData;
      if (anyTaskData) {
        const taskSqlStart = performance.now();
        console.log("Görev verileri SQL'den yükleniyor...");
        const toMs = (iso?: string | null): number => (iso ? new Date(iso).getTime() : 0);

        const taskSqlPromise = (async () => {
          // 1) Tüm görev tanımları (single/daily → tasks; weekly/monthly → ayrı düğümler)
          const allDefs: any[] = [];
          let tp = 1;
          for (;;) {
            const res = await taskDataService.listTasks({ page: tp, pageSize: 500 });
            allDefs.push(...res.items);
            if (res.items.length === 0 || allDefs.length >= (res.totalCount || 0)) break;
            tp++;
          }
          const td: any = {};
          const wt: any = {};
          const mt: any = {};
          allDefs.forEach((t) => {
            const key = String(t.id);
            if (t.taskType === 'weekly') { wt[key] = { personnelId: t.personnelFirebaseUid || null }; return; }
            if (t.taskType === 'monthly') { mt[key] = { personnelId: t.personnelFirebaseUid || null }; return; }
            td[key] = {
              name: t.name,
              description: t.description || '',
              status: t.status || 'pending',
              personnelId: t.personnelFirebaseUid || null,
              isRecurring: !!t.isRecurring,
              completionType: t.completionType || 'button',
              createdAt: toMs(t.createdAt),
              deadline: t.deadline || undefined,
              completedAt: t.completedAt ? toMs(t.completedAt) : undefined,
            };
          });
          tasksData = td;
          companyData.weeklyTasks = wt;
          companyData.monthlyTasks = mt;

          // 2) Tamamlama kayıtları — şirket geneli, sayfalı
          const fetchCompletions = async (status: 'completed' | 'missed') => {
            const items: any[] = [];
            let cp = 1;
            for (;;) {
              const res = await taskDataService.listCompletions({ status, page: cp, pageSize: 500 });
              items.push(...res.items);
              if (res.items.length === 0 || items.length >= (res.totalCount || 0)) break;
              cp++;
            }
            return items;
          };

          // RTDB-uyumlu {taskId: {date: {time: record}}} yapısı; taskType'a göre kovala
          const bucket = (items: any[], isCompleted: boolean) => {
            const single: any = {}; const weekly: any = {}; const monthly: any = {};
            items.forEach((c) => {
              const date = c.taskDate;
              if (!date) return;
              const key = String(c.taskId);
              const time = c.taskTime || '';
              const rec: any = {
                taskName: c.taskName || '',
                taskDescription: c.taskDescription || '',
                personnelId: c.personnelFirebaseUid || null,
                completedBy: c.personnelFirebaseUid || null,
              };
              if (isCompleted) rec.completedAt = toMs(c.completedAt);
              else rec.missedAt = toMs(c.missedAt);
              if (c.locationLat != null && c.locationLng != null) {
                rec.completionLocation = {
                  latitude: c.locationLat,
                  longitude: c.locationLng,
                  accuracy: c.locationAccuracy ?? undefined,
                };
              }
              const target = c.taskType === 'weekly' ? weekly : c.taskType === 'monthly' ? monthly : single;
              if (!target[key]) target[key] = {};
              if (!target[key][date]) target[key][date] = {};
              target[key][date][time] = rec;
            });
            return { single, weekly, monthly };
          };

          if (needsCompletedTasksData || needsWeeklyTasksData || needsMonthlyTasksData) {
            const comp = bucket(await fetchCompletions('completed'), true);
            companyData.completedTasks = comp.single;
            companyData.completedWeeklyTasks = comp.weekly;
            companyData.completedMonthlyTasks = comp.monthly;
          }
          if (needsMissedTasksData || needsWeeklyTasksData || needsMonthlyTasksData) {
            const miss = bucket(await fetchCompletions('missed'), false);
            companyData.missedTasks = miss.single;
            companyData.missedWeeklyTasks = miss.weekly;
            companyData.missedMonthlyTasks = miss.monthly;
          }

          console.log(`Görev verisi SQL çekme tamamlandı: ${performance.now() - taskSqlStart}ms`);
        })().catch(err => {
          console.error('SQL görev verisi alınamadı:', err);
        });
        promises.push(taskSqlPromise);
      } else {
        console.log("Görev verileri atlandı - gerekli widget seçilmemiş");
      }
      
      // Tüm promise'ların tamamlanmasını bekleyelim
      const promisesStart = performance.now();
      await Promise.all(promises);
      console.log(`Tüm verilerin çekilme süresi: ${performance.now() - promisesStart}ms`);
      
      console.log("Veri yükleme tamamlandı, veriler işleniyor...");
      
      // Verileri işle
      const processStart = performance.now();
      processData(personnelData, tasksData, companyData);
      console.log(`Verilerin işlenme süresi: ${performance.now() - processStart}ms`);
      
      console.log(`Toplam veri yükleme ve işleme süresi: ${performance.now() - loadStart}ms`);
      console.timeEnd('Load-Data-Based-On-Widgets');
    } catch (error) {
      console.error('Widget bazlı veri yükleme hatası:', error);
      setError('Veri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
      console.timeEnd('Dashboard-Total');
      console.log('Dashboard yüklenmesi tamamlandı:', new Date().toISOString());
    }
  };
  
  // Widget seçimleri veya firma ID'si değişince veri çek
  useEffect(() => {
    console.log('Widget seçimleri useEffect çalıştı', selectedWidgets.length);
    if (companyId) {
      console.log("Widget seçimleri veya branchId değişti, veriler yeniden yükleniyor..."); // Konsol mesajı güncellendi
      loadDataBasedOnSelectedWidgets(companyId);
    }
    
    // Temizlik işlevi (boş)
    return () => {};
  }, [companyId, selectedWidgets, branchId]); // branchId bağımlılık olarak eklendi
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Görev durumu grafiği için renkler
  const COLORS = [THEME_COLORS.completed, '#1976D2', '#2196F3', THEME_COLORS.pending, '#F44336', '#9E9E9E'];
  
  // Personel performans grafiği için renkler
  const PERFORMANCE_COLORS = {
    completed: THEME_COLORS.completed, // Tamamlanan görevler için yeşil
    pending: '#1976D2',                // Devam eden görevler için mavi
    missed: '#F44336'                  // Kaçırılan görevler için kırmızı
  };
  
  const CustomTooltip = ({ active, payload, label, performanceChart }: any) => {
    if (active && payload && payload.length) {
      if (performanceChart) {
        return (
          <Box sx={{ bgcolor: 'background.paper', p: 2, boxShadow: 3, borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold">{payload[0]?.payload.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: PERFORMANCE_COLORS.completed, borderRadius: '50%' }} />
              <Typography variant="body2">Tamamlanan: {payload[0]?.value}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: PERFORMANCE_COLORS.pending, borderRadius: '50%' }} />
              <Typography variant="body2">Devam Eden: {payload[1]?.value}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: PERFORMANCE_COLORS.missed, borderRadius: '50%' }} />
              <Typography variant="body2">Kaçırılan: {payload[2]?.value}</Typography>
            </Box>
          </Box>
        );
      }
      
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 2, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">{payload[0].name}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: payload[0].payload.color, borderRadius: '50%' }} />
            <Typography variant="body2">{payload[0].value} Görev</Typography>
          </Box>
        </Box>
      );
    }
    
    return null;
  };
  
  // Modal'ı aç
  const handleOpenWidgetSelector = () => {
    setWidgetSelectorOpen(true);
  };

  // Modal'ı kapat
  const handleCloseWidgetSelector = () => {
    setWidgetSelectorOpen(false);
  };

  // Widget listesi
  const widgets = [
    {
      id: 'normalTaskStatusChart',
      title: t('dashboard.normalTaskStatusChart.title'),
      description: t('dashboard.normalTaskStatusChart.modalDescription')
    },
    {
      id: 'normalTaskPersonnelPerformance',
      title: t('dashboard.normalTaskPersonnelPerformanceChart.title'),
      description: t('dashboard.normalTaskPersonnelPerformanceChart.modalDescription')
    },
    {
      id: 'statsDaily',
      title: t('dashboard.stats.totalPersonnel'),
      description: t('dashboard.stats.activeTasks')
    },
    {
      id: 'statsWeeklyMonthly',
      title: t('dashboard.stats.totalWeeklyTasks'),
      description: t('dashboard.stats.totalMonthlyTasks')
    },
    {
      id: 'taskStatus',
      title: t('dashboard.taskStatusChart.title'),
      description: t('dashboard.taskStatusChart.modalDescription')
    },
    {
      id: 'weeklyTaskStatus',
      title: t('dashboard.weeklyTaskStatusChart.title'),
      description: t('dashboard.weeklyTaskStatusChart.modalDescription')
    },
    {
      id: 'monthlyTaskStatus',
      title: t('dashboard.monthlyTaskStatusChart.title'),
      description: t('dashboard.monthlyTaskStatusChart.modalDescription')
    },
    {
      id: 'personnelPerformance',
      title: t('dashboard.personnelPerformanceChart.title'),
      description: t('dashboard.personnelPerformanceChart.modalDescription')
    },
    {
      id: 'weeklyPersonnelPerformance',
      title: t('dashboard.weeklyPersonnelPerformanceChart.title'),
      description: t('dashboard.weeklyPersonnelPerformanceChart.modalDescription')
    },
    {
      id: 'monthlyPersonnelPerformance',
      title: t('dashboard.monthlyPersonnelPerformanceChart.title'),
      description: t('dashboard.monthlyPersonnelPerformanceChart.modalDescription')
    },
    {
      id: 'surveyCharts',
      title: 'Anket Grafikleri',
      description: 'Genel anket memnuniyet oranı ve son 1 aylık memnuniyet trendini gösterir.'
    },
    {
      id: 'missedTasks',
      title: t('dashboard.missedTasksWidget.title'),
      description: t('dashboard.missedTasksModal.title')
    },
    {
      id: 'completedTasks',
      title: t('dashboard.completedTasksWidget.title'),
      description: t('dashboard.completedTasksModal.title')
    },
    {
      id: 'missedTasksNormal',
      title: t('dashboard.missedTasksNormalWidget.title'),
      description: t('dashboard.missedTasksNormalModal.title')
    },
    {
      id: 'completedTasksNormal',
      title: t('dashboard.completedTasksNormalWidget.title'),
      description: t('dashboard.completedTasksNormalModal.title')
    },
    {
      id: 'worstPerformers',
      title: t('dashboard.worstPerformersWidget.title'),
      description: t('dashboard.worstPerformersWidget.noData')
    },
    {
      id: 'bestPerformers',
      title: t('dashboard.bestPerformersWidget.title'),
      description: t('dashboard.bestPerformersWidget.noData')
    },
    {
      id: 'taskLocations',
      title: t('dashboard.taskLocations.title'),
      description: t('dashboard.taskLocations.description')
    }
  ];
  
  return (
    <ScrollableContent>
      {/* Widget Yönetim Butonu */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        backgroundColor: 'background.paper',
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h5" fontWeight="bold">
          {t('dashboard.dashboardTitle')}
              </Typography>
        <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          onClick={handleOpenWidgetSelector}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {t('dashboard.manageWidgets')}
        </Button>
      </Box>

      {/* İstatistik Kartları */}
      {selectedWidgets.includes('statsDaily') && (
        <StatsCards
          stats={stats}
          isManager={isManager}
          onPersonnelClick={handleNavigateToPersonnel}
          onTasksClick={handleNavigateToTasks}
          onPendingTasksClick={handleOpenPendingTasksModal}
          onCompletedTasksClick={handleOpenAllCompletedTasksModal}
          onWeeklyPendingTasksClick={handleNavigateToWeeklyTasks}
          onWeeklyCompletedTasksClick={handleNavigateToWeeklyTasks}
          onMonthlyPendingTasksClick={handleNavigateToMonthlyTasks}
          onMonthlyCompletedTasksClick={handleNavigateToMonthlyTasks}
          showDailyStats={true}
          showWeeklyMonthly={false}
        />
      )}
      
      {selectedWidgets.includes('statsWeeklyMonthly') && (
        <StatsCards
          stats={stats}
          isManager={isManager}
          onPersonnelClick={handleNavigateToPersonnel}
          onTasksClick={handleNavigateToTasks}
          onPendingTasksClick={handleOpenPendingTasksModal}
          onCompletedTasksClick={handleOpenAllCompletedTasksModal}
          onWeeklyPendingTasksClick={handleNavigateToWeeklyTasks}
          onWeeklyCompletedTasksClick={handleNavigateToWeeklyTasks}
          onMonthlyPendingTasksClick={handleNavigateToMonthlyTasks}
          onMonthlyCompletedTasksClick={handleNavigateToMonthlyTasks}
          showDailyStats={false}
          showWeeklyMonthly={true}
        />
      )}
      
      {/* Grafikler */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {selectedWidgets.includes('taskStatus') && (
          <Grid item xs={12} md={6} className="task-status-section">
            <TaskStatusChart 
              taskStatusData={taskStatusData} 
            />
          </Grid>
        )}
        
        {selectedWidgets.includes('weeklyTaskStatus') && (
          <Grid item xs={12} md={6} className="weekly-task-status-section">
            <WeeklyTaskStatusChart 
              weeklyTaskStatusData={weeklyTaskStatusData} 
            />
          </Grid>
        )}

        {selectedWidgets.includes('monthlyTaskStatus') && (
          <Grid item xs={12} md={6} className="monthly-task-status-section">
            <MonthlyTaskStatusChart 
              monthlyTaskStatusData={monthlyTaskStatusData} 
            />
          </Grid>
        )}
        
        {selectedWidgets.includes('normalTaskStatusChart') && (
          <Grid item xs={12} md={6} className="normal-task-status-section">
            <SurveyCharts 
              branchId={branchId} 
              isManager={isManager} 
              selectedWidgets={['normalTaskStatusChart']} 
            />
          </Grid>
        )}
        
        {selectedWidgets.includes('personnelPerformance') && (
        <Grid item xs={12} md={6} className="personnel-performance-section">
            <PersonnelPerformanceChart 
              personnelPerformanceData={personnelPerformanceData} 
            />
          </Grid>
        )}
        
        {selectedWidgets.includes('weeklyPersonnelPerformance') && (
        <Grid item xs={12} md={6} className="weekly-personnel-performance-section">
            <WeeklyPersonnelPerformanceChart 
              weeklyPersonnelPerformanceData={weeklyPersonnelPerformanceData} 
            />
          </Grid>
        )}

        {selectedWidgets.includes('monthlyPersonnelPerformance') && (
        <Grid item xs={12} md={6} className="monthly-personnel-performance-section">
            <MonthlyPersonnelPerformanceChart 
              monthlyPersonnelPerformanceData={monthlyPersonnelPerformanceData} 
            />
          </Grid>
        )}

        {selectedWidgets.includes('normalTaskPersonnelPerformance') && (
        <Grid item xs={12} md={6} className="normal-task-personnel-performance-section">
            <SurveyCharts 
              branchId={branchId} 
              isManager={isManager} 
              selectedWidgets={['normalTaskPersonnelPerformance']} 
            />
          </Grid>
        )}
        
        {/* Anket Grafikleri */}
        {selectedWidgets.includes('surveyCharts') && (
          <Grid item xs={12} className="survey-charts-section">
            <SurveyCharts 
              branchId={branchId} 
              isManager={isManager} 
              selectedWidgets={selectedWidgets}
            />
          </Grid>
        )}
      </Grid>
      
      
      
      {/* Liste Kartları */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {selectedWidgets.includes('missedTasks') && (
        <Grid item xs={12} md={6} className="missed-tasks-section">
          <StyledCard>
            <CardHeader 
              title={t('dashboard.missedTasksWidget.title')}
              titleTypographyProps={{ fontWeight: 'bold' }}
              action={
                <Button 
                  variant="text" 
                  color="primary" 
                  onClick={handleOpenMissedTasksModal}
                  size="small"
                >
                  {t('dashboard.missedTasksWidget.viewAll')}
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {recentMissedTasks.length === 0 ? (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                  {t('dashboard.missedTasksWidget.noTasks')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('dashboard.missedTasksWidget.taskName')}</TableCell>
                        <TableCell>{t('dashboard.missedTasksWidget.personnel')}</TableCell>
                        <TableCell>{t('dashboard.missedTasksWidget.date')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentMissedTasks.map((task) => (
                        <TableRow key={task.id} hover>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {task.name + " - " + t('dashboard.missedTasksWidget.recurring')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {task.personnelName && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon fontSize="small" color="primary" />
                                <Typography variant="body2" noWrap>
                                  {task.personnelName}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {`${task.time} - ${task.date}`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
        )}
        {selectedWidgets.includes('completedTasks') && (
        <Grid item xs={12} md={6} className="completed-tasks-section">
          <StyledCard>
            <CardHeader 
              title={t('dashboard.completedTasksWidget.title')} 
              titleTypographyProps={{ fontWeight: 'bold' }}
              action={
                <Button 
                  variant="text" 
                  color="primary" 
                  onClick={handleOpenCompletedTasksModal}
                  size="small"
                >
                  {t('dashboard.completedTasksWidget.viewAll')}
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {recentCompletedTasks.length === 0 ? (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                  {t('dashboard.completedTasksWidget.noTasks')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('dashboard.completedTasksWidget.taskName')}</TableCell>
                        <TableCell>{t('dashboard.completedTasksWidget.personnel')}</TableCell>
                        <TableCell>{t('dashboard.completedTasksWidget.date')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentCompletedTasks.map((task) => (
                        <TableRow key={task.id} hover>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {task.name + " - " + t('dashboard.completedTasksWidget.recurring')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {task.personnelName && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon fontSize="small" color="success" />
                                <Typography variant="body2" noWrap>
                                  {task.personnelName}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {`${task.time} - ${task.date}`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
        )}

        {selectedWidgets.includes('missedTasksNormal') && (
        <Grid item xs={12} md={6} className="missed-tasks-normal-section">
          <StyledCard>
            <CardHeader 
              title={t('dashboard.missedTasksNormalWidget.title')}
              titleTypographyProps={{ fontWeight: 'bold' }}
              action={
                <Button 
                  variant="text" 
                  color="primary" 
                  onClick={handleOpenMissedTasksNormalModal}
                  size="small"
                >
                  {t('dashboard.missedTasksNormalWidget.viewAll')}
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {recentMissedTasksNormal.length === 0 ? (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                  {t('dashboard.missedTasksNormalWidget.noTasks')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('dashboard.missedTasksNormalWidget.taskName')}</TableCell>
                        <TableCell>{t('dashboard.missedTasksNormalWidget.personnel')}</TableCell>
                        <TableCell>{t('dashboard.missedTasksNormalWidget.date')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentMissedTasksNormal.map((task) => (
                        <TableRow key={task.id} hover>
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            <Typography variant="body2">
                              {task.name + " - " + t('dashboard.missedTasksNormalWidget.normal')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {task.personnelName && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon fontSize="small" color="primary" />
                                <Typography variant="body2" noWrap>
                                  {task.personnelName}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary" noWrap>
                              {task.date} {task.time}
                            </Typography>
                            {task.completedDate && (
                              <Typography variant="caption" color="error" display="block">
                                {task.completedDate} {task.completedTime}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
        )}

        {selectedWidgets.includes('completedTasksNormal') && (
        <Grid item xs={12} md={6} className="completed-tasks-normal-section">
          <StyledCard>
            <CardHeader 
              title={t('dashboard.completedTasksNormalWidget.title')}
              titleTypographyProps={{ fontWeight: 'bold' }}
              action={
                <Button 
                  variant="text" 
                  color="primary" 
                  onClick={handleOpenCompletedTasksNormalModal}
                  size="small"
                >
                  {t('dashboard.completedTasksNormalWidget.viewAll')}
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {recentCompletedTasksNormal.length === 0 ? (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                  {t('dashboard.completedTasksNormalWidget.noTasks')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('dashboard.completedTasksNormalWidget.taskName')}</TableCell>
                        <TableCell>{t('dashboard.completedTasksNormalWidget.personnel')}</TableCell>
                        <TableCell>{t('dashboard.completedTasksNormalWidget.date')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentCompletedTasksNormal.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            <Typography variant="body2">
                              {task.name + " - " + t('dashboard.completedTasksNormalWidget.normal')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {task.personnelName && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon fontSize="small" color="success" />
                                <Typography variant="body2" noWrap>
                                  {task.personnelName}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary" noWrap>
                              {task.date} {task.time}
                            </Typography>
                            {task.completedDate && (
                              <Typography variant="caption" color="error" display="block">
                                {task.completedDate} {task.completedTime}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
        )}
      </Grid>
      
      {/* En Çok Görev Geciktiren ve Yapan Personeller */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {selectedWidgets.includes('worstPerformers') && (
        <Grid item xs={12} md={6} className="worst-performers-section">
          <StyledCard>
            <CardHeader 
              title={t('dashboard.worstPerformersWidget.title')}
              titleTypographyProps={{ fontWeight: 'bold' }}
            />
            <Divider />
            <CardContent>
              {worstPerformers.length === 0 ? (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                  {t('dashboard.worstPerformersWidget.noData')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('dashboard.worstPerformersWidget.personnel')}</TableCell>
                        <TableCell>{t('dashboard.worstPerformersWidget.missedTaskCount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {worstPerformers.slice(0, 10).map((person, index) => (
                        <TableRow key={person.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Avatar sx={{ width: 24, height: 24, bgcolor: index < 3 ? '#F44336' : theme.palette.grey[500] }}>
                                <PersonIcon fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" noWrap>
                                {person.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium" color={index < 3 ? 'error' : 'inherit'}>
                              {person.missedCount} {t('dashboard.worstPerformersWidget.tasks')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
        )}
        
        {selectedWidgets.includes('bestPerformers') && (
        <Grid item xs={12} md={6} className="best-performers-section">
          <StyledCard>
            <CardHeader 
              title={t('dashboard.bestPerformersWidget.title')}
              titleTypographyProps={{ fontWeight: 'bold' }}
            />
            <Divider />
            <CardContent>
              {personnelPerformanceData.length === 0 ? (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                  {t('dashboard.bestPerformersWidget.noData')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('dashboard.bestPerformersWidget.personnel')}</TableCell>
                        <TableCell>{t('dashboard.bestPerformersWidget.completedTaskCount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {personnelPerformanceData
                        .sort((a, b) => b.completed - a.completed)
                        .slice(0, 10)
                        .map((person, index) => (
                          <TableRow key={person.name} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Avatar sx={{ width: 24, height: 24, bgcolor: index < 3 ? THEME_COLORS.completed : theme.palette.grey[500] }}>
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Typography variant="body2" noWrap>
                                  {person.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium" color={index < 3 ? 'success.main' : 'inherit'}>
                                {person.completed} {t('dashboard.bestPerformersWidget.tasks')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
        )}
      </Grid>
      
      {/* Harita */}
      {selectedWidgets.includes('taskLocations') && (
      <MapCard sx={{ mb: 2 }} className="task-locations-section">
        <CardHeader 
          title={t('dashboard.taskLocationMap.title')}
          titleTypographyProps={{ fontWeight: 'bold' }}
          avatar={<Avatar sx={{ bgcolor: THEME_COLORS.completed }}><LocationIcon /></Avatar>}
        />
        <Divider />
        
        <FilterContainer>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>{t('dashboard.taskLocationMap.recentTasks')}</InputLabel>
            <Select
              value={selectedTaskCount}
              label={t('dashboard.taskLocationMap.recentTasks')}
              onChange={handleTaskCountChange}
            >
              <MenuItem value={10}>{t('dashboard.taskLocationMap.last10Tasks')}</MenuItem>
              <MenuItem value={20}>{t('dashboard.taskLocationMap.last20Tasks')}</MenuItem>
              <MenuItem value={30}>{t('dashboard.taskLocationMap.last30Tasks')}</MenuItem>
              <MenuItem value={50}>{t('dashboard.taskLocationMap.last50Tasks')}</MenuItem>
              <MenuItem value={100}>{t('dashboard.taskLocationMap.last100Tasks')}</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 200, maxWidth: 300 }}>
            <InputLabel>{t('dashboard.taskLocationMap.filterPersonnel')}</InputLabel>
            <Select
              multiple
              value={selectedPersonnel}
              label={t('dashboard.taskLocationMap.filterPersonnel')}
              onChange={handlePersonnelChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => {
                    const person = personnel.find(p => p.id === value);
                    return (
                      <Chip 
                        key={value} 
                        label={person ? person.name : value} 
                        size="small" 
                      />
                    );
                  })}
                </Box>
              )}
              onClose={() => setPersonnelSearchTerm('')}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 250,
                    overflow: 'auto'
                  }
                }
              }}
            >
              <ListItem>
                <TextField
                  size="small"
                  autoFocus
                  placeholder={t('dashboard.taskLocationMap.searchPersonnel')}
                  fullWidth
                  value={personnelSearchTerm}
                  onChange={(e) => setPersonnelSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </ListItem>
              {personnel
                .filter(person => 
                  personnelSearchTerm === '' || 
                  person.name.toLowerCase().includes(personnelSearchTerm.toLowerCase())
                )
                .map((person) => (
                  <MenuItem key={person.id} value={person.id}>
                    <Checkbox checked={selectedPersonnel.indexOf(person.id) > -1} />
                    <ListItemText primary={person.name} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 200, maxWidth: 300 }}>
            <InputLabel>{t('dashboard.taskLocationMap.filterTasks')}</InputLabel>
            <Select
              multiple
              value={selectedTasks}
              label={t('dashboard.taskLocationMap.filterTasks')}
              onChange={handleTaskChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => {
                    const task = tasks.find(t => t.id === value);
                    return (
                      <Chip 
                        key={value} 
                        label={task ? task.name : value} 
                        size="small" 
                      />
                    );
                  })}
                </Box>
              )}
              onClose={() => setTaskSearchTerm('')}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 250,
                    overflow: 'auto'
                  }
                }
              }}
            >
              <ListItem>
                <TextField
                  size="small"
                  autoFocus
                  placeholder={t('dashboard.taskLocationMap.searchTasks')}
                  fullWidth
                  value={taskSearchTerm}
                  onChange={(e) => setTaskSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </ListItem>
              {tasks
                .filter(task => 
                  taskSearchTerm === '' || 
                  task.name.toLowerCase().includes(taskSearchTerm.toLowerCase())
                )
                .map((task) => (
                  <MenuItem key={task.id} value={task.id}>
                    <Checkbox checked={selectedTasks.indexOf(task.id) > -1} />
                    <ListItemText primary={task.name} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={handleClearFilters}
            sx={{ height: 40 }}
          >
            {t('dashboard.taskLocationMap.clearFilters')}
          </Button>
          
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.taskLocationMap.totalLocations').replace('{0}', filteredLocations.length.toString())}
            </Typography>
          </Box>
        </FilterContainer>
        
        <MapContainerWrapper>
          {filteredLocations.length === 0 ? (
            <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography color="textSecondary">
                {t('dashboard.taskLocations.noData')}
              </Typography>
            </Box>
          ) : (
            <MapContainer 
              style={{ height: '100%', width: '100%' }}
              zoom={13} 
              scrollWheelZoom={true}
            >
              <MapView 
                center={
                  filteredLocations.length > 0 
                    ? [filteredLocations[0].location.latitude, filteredLocations[0].location.longitude] as [number, number]
                    : [39.9334, 32.8597] as [number, number]
                } 
                zoom={13} 
              />
              
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {filteredLocations.map((task, index) => {
                  const icon = createColoredMarkerIcon(getPersonnelColor(index));
                return (
                  <Marker 
                    key={task.id} 
                    position={[task.location.latitude, task.location.longitude] as [number, number]} 
                    icon={icon}
                  >
                    <Tooltip permanent={false} direction="top">
                      <strong>{task.name}</strong><br />
                      {task.personnelName}
                    </Tooltip>
                    <Popup>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{task.name}</Typography>
                        <Typography variant="body2">{task.description}</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2">
                          <strong>{t('dashboard.taskLocationMap.popupLabels.personnel')}:</strong> {task.personnelName}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('dashboard.taskLocationMap.popupLabels.date')}:</strong> {task.date}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('dashboard.taskLocationMap.popupLabels.time')}:</strong> {task.time}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('dashboard.taskLocationMap.popupLabels.location')}:</strong> {task.location.latitude.toFixed(5)}, {task.location.longitude.toFixed(5)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('dashboard.taskLocationMap.popupLabels.accuracy')}:</strong> {task.location.accuracy ? `±${task.location.accuracy.toFixed(1)}m` : t('dashboard.taskLocationMap.popupLabels.unspecified')}
                        </Typography>
                      </Box>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </MapContainerWrapper>
      </MapCard>
      )}
      
      {/* Widget Seçim Modalı */}
      <WidgetSelectorModal
        open={widgetSelectorOpen}
        onClose={handleCloseWidgetSelector}
        selectedWidgets={selectedWidgets}
        onWidgetSelectionChange={handleWidgetSelectionChange}
        widgets={widgets}
      />
      
      {/* Modal Pencereler */}
      <MissedTasksModal 
        open={missedTasksModalOpen} 
        onClose={handleCloseMissedTasksModal} 
        tasks={allMissedTasks} 
      />
      
      <CompletedTasksModal 
        open={completedTasksModalOpen} 
        onClose={handleCloseCompletedTasksModal} 
        tasks={allCompletedTasks} 
      />
      
      {/* Yeni modallar */}
      <PendingTasksModal 
        open={pendingTasksModalOpen} 
        onClose={handleClosePendingTasksModal} 
        tasks={allPendingTasks} 
      />
      
      <AllCompletedTasksModal 
        open={allCompletedTasksModalOpen} 
        onClose={handleCloseAllCompletedTasksModal} 
        tasks={[
          // Ana görev listesindeki tamamlanmış görevler
          ...tasks.filter(task => task.status === 'completed').map(task => {
            // Personel bilgisini bul
            const personel = personnel.find(p => p.id === task.personnelId);
            return {
              id: task.id,
              name: task.name,
              description: task.description || '',
              // Tamamlanma zamanı varsa kullan, yoksa oluşturulma zamanını kullan
              completedAt: task.completedAt || task.createdAt,
              personnelId: task.personnelId,
              personnelName: personel ? personel.name : 'Atanmamış'
            };
          }),
          // Veritabanından çekilen completedTasks koleksiyonundaki görevler
          ...allCompletedTasks
        ].sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))} // En son tamamlananlar en üstte
      />
      
      <Dialog open={missedTasksNormalModalOpen} onClose={handleCloseMissedTasksNormalModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          {t('dashboard.missedTasksNormalModal.title')}
          <IconButton
            aria-label={t('sidebar.closeButton')}
            onClick={handleCloseMissedTasksNormalModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('dashboard.missedTasksNormalModal.taskName')}</TableCell>
                  <TableCell>{t('dashboard.missedTasksNormalModal.taskDate')}</TableCell>
                  <TableCell>{t('dashboard.missedTasksNormalModal.completionDate')}</TableCell>
                  <TableCell>{t('dashboard.missedTasksNormalModal.personnel')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allMissedTasksNormal.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.name}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{task.date} {task.time}</TableCell>
                    <TableCell>{task.completedDate ? `${task.completedDate} ${task.completedTime}` : '-'}</TableCell>
                    <TableCell>{task.personnelName}</TableCell>
                  </TableRow>
                ))}
                {allMissedTasksNormal.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">{t('dashboard.missedTasksNormalModal.noTasks')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      <Dialog open={completedTasksNormalModalOpen} onClose={handleCloseCompletedTasksNormalModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          {t('dashboard.completedTasksNormalModal.title')}
          <IconButton
            aria-label={t('sidebar.closeButton')}
            onClick={handleCloseCompletedTasksNormalModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('dashboard.completedTasksNormalModal.taskName')}</TableCell>
                  <TableCell>{t('dashboard.completedTasksNormalModal.taskDate')}</TableCell>
                  <TableCell>{t('dashboard.completedTasksNormalModal.completionDate')}</TableCell>
                  <TableCell>{t('dashboard.completedTasksNormalModal.personnel')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allCompletedTasksNormal.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.name}</TableCell>
                    <TableCell sx={{ color: 'primary.main' }}>{task.date} {task.time}</TableCell>
                    <TableCell sx={{ color: 'success.main' }}>{task.completedDate ? `${task.completedDate} ${task.completedTime}` : '-'}</TableCell>
                    <TableCell>{task.personnelName}</TableCell>
                  </TableRow>
                ))}
                {allCompletedTasksNormal.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">{t('dashboard.completedTasksNormalModal.noTasks')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>


    </ScrollableContent>
  );
};

// Personel renklerini belirle
const getPersonnelColor = (index: number) => {
  const colors = [
    '#FF6B6B', // Kırmızı
    '#4ECDC4', // Turkuaz
    '#45B7D1', // Mavi
    '#96CEB4', // Yeşil
    '#FFEEAD', // Sarı
    '#D4A5A5', // Pembe
    '#9B59B6', // Mor
    '#E67E22', // Turuncu
    '#1ABC9C', // Deniz Yeşili
    '#3498DB'  // Açık Mavi
  ];
  return colors[index % colors.length];
};

export default Dashboard; 
